import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import {
  Activity,
  CheckCircle2,
  Clock3,
  Pause,
  Play,
  Plus,
  QrCode,
  ShieldCheck,
  Users,
  XCircle,
} from 'lucide-react';
import { api } from '@/services/api';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { EmptyState } from '@/components/ui/empty-state';
import { useToast } from '@/components/ui/toast';
import { RidePassQr } from './ride-pass-qr';

function toneForState(state) {
  if (state === 'active') return 'success';
  if (state === 'paused') return 'warning';
  if (state === 'revoked' || state === 'expired' || state === 'exhausted') return 'error';
  return 'neutral';
}

function timeLeft(expiresAt) {
  const milliseconds = new Date(expiresAt).getTime() - Date.now();
  if (milliseconds <= 0) return 'Expired';
  const hours = Math.ceil(milliseconds / 3600000);
  return hours < 48 ? `${hours}h left` : `${Math.ceil(hours / 24)}d left`;
}

function InviteMetric({ icon: Icon, label, value, tone = 'cyan' }) {
  return <div className="invite-metric">
    <span className={`invite-metric__icon invite-metric__icon--${tone}`}><Icon size={15}/></span>
    <span><small>{label}</small><strong>{value}</strong></span>
  </div>;
}

export function InviteHubPage() {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const query = useQuery({
    queryKey: ['invite-hub'],
    queryFn: () => api('/api/v1/admin/invite-hub').then((result) => result.data || []),
    refetchInterval: 20_000,
  });
  const passes = Array.isArray(query.data) ? query.data : [];
  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return passes.filter((pass) => !needle || [pass.title, pass.code, pass.inviteType, pass.state].some((value) => String(value || '').toLowerCase().includes(needle)));
  }, [passes, search]);
  const selected = passes.find((pass) => pass.id === selectedId) || filtered[0];
  const totals = useMemo(() => ({
    active: passes.filter((pass) => pass.state === 'active').length,
    joined: passes.reduce((sum, pass) => sum + Number(pass.analytics?.accepted || 0), 0),
    pending: passes.reduce((sum, pass) => sum + Number(pass.analytics?.pending || 0), 0),
    checkedIn: passes.reduce((sum, pass) => sum + Number(pass.analytics?.checkedIn || 0), 0),
  }), [passes]);

  const setStatus = async (pass, status) => {
    await api(`/api/v1/admin/invite-hub/passes/${pass.id}/status`, {
      method: 'PATCH',
      body: JSON.stringify({ status }),
    });
    await queryClient.invalidateQueries({ queryKey: ['invite-hub'] });
    toast.show(status === 'active' ? 'Ride Pass resumed' : status === 'paused' ? 'Ride Pass paused' : 'Ride Pass revoked', 'success');
  };

  return <div className="invite-hub-page workspace-page">
    <PageHeader
      eyebrow="Network access"
      title="Invite Hub"
      description="Create secure Ride Passes for clubs and convoys."
      actions={<Button onClick={() => navigate('/invite-hub/create')}><Plus size={15}/>Create Ride Pass</Button>}
    />

    <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
      <InviteMetric icon={QrCode} label="Active passes" value={totals.active}/>
      <InviteMetric icon={Users} label="Joined" value={totals.joined} tone="violet"/>
      <InviteMetric icon={Clock3} label="Awaiting approval" value={totals.pending} tone="amber"/>
      <InviteMetric icon={CheckCircle2} label="Checked in" value={totals.checkedIn} tone="green"/>
    </div>

    <div className="invite-hub-grid workspace-fill">
      <section className="invite-hub-list">
        <div className="border-b border-white/8 p-3">
          <Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search Ride Pass"/>
        </div>
        <div className="scrollbar min-h-0 flex-1 overflow-y-auto">
          {filtered.map((pass) => <button
            type="button"
            key={pass.id}
            onClick={() => setSelectedId(pass.id)}
            className={`invite-pass-row ${selected?.id === pass.id ? 'invite-pass-row--active' : ''}`}
          >
            <span className="invite-pass-row__glyph"><QrCode size={17}/></span>
            <span className="min-w-0 flex-1">
              <span className="flex items-center gap-2"><strong className="truncate">{pass.title}</strong><Badge tone={toneForState(pass.state)}>{pass.state}</Badge></span>
              <span className="mt-1 block truncate text-[10px] text-slate-500">{String(pass.inviteType).replaceAll('_', ' ')} · {pass.code}</span>
              <span className="mt-2 flex gap-3 text-[9px] text-slate-600"><span>{pass.usageCount}/{pass.usageLimit} used</span><span>{timeLeft(pass.expiresAt)}</span></span>
            </span>
          </button>)}
          {!filtered.length && <EmptyState title={query.isLoading ? 'Loading Ride Passes...' : 'No Ride Passes'} description="Create a QR Ride Pass for a club or convoy."/>}
        </div>
      </section>

      <section className="invite-hub-preview">
        {!selected ? <EmptyState title="Select a Ride Pass"/> : <>
          <div className="invite-hub-preview__header">
            <div><p className="eyebrow">Convoy Ride Pass</p><h2>{selected.title}</h2><p>{String(selected.inviteType).replaceAll('_', ' ')} · {selected.code}</p></div>
            <Badge tone={toneForState(selected.state)}>{selected.state}</Badge>
          </div>
          <div className="invite-hub-preview__body">
            <RidePassQr value={selected.joinUrl} title={selected.title} code={selected.code}/>
            <div className="space-y-3">
              <div className="invite-pass-detail-grid">
                <InviteMetric icon={Users} label="Used" value={`${selected.usageCount}/${selected.usageLimit}`}/>
                <InviteMetric icon={Clock3} label="Validity" value={timeLeft(selected.expiresAt)} tone="amber"/>
                <InviteMetric icon={ShieldCheck} label="Approval" value={selected.approvalRequired ? 'Required' : 'Instant'} tone="green"/>
                <InviteMetric icon={Activity} label="Check-ins" value={selected.analytics?.checkedIn || 0} tone="violet"/>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button variant="secondary" onClick={() => navigate(`/invite-hub/${selected.id}`)}>Open details</Button>
                {selected.state === 'active' && <Button variant="secondary" onClick={() => void setStatus(selected, 'paused')}><Pause size={14}/>Pause</Button>}
                {selected.state === 'paused' && <Button variant="secondary" onClick={() => void setStatus(selected, 'active')}><Play size={14}/>Resume</Button>}
                {!['revoked', 'expired'].includes(selected.state) && <Button variant="danger" onClick={() => void setStatus(selected, 'revoked')}><XCircle size={14}/>Revoke</Button>}
              </div>
            </div>
          </div>
          <div className="invite-hub-activity">
            <div className="flex items-center justify-between"><h3>Recent activity</h3><Link to={`/invite-hub/${selected.id}`} className="text-[10px] font-bold text-cyan-300">View all</Link></div>
            <div className="mt-3 space-y-2">
              {(selected.redemptions || []).slice(0, 5).map((item) => <div key={item._id} className="invite-activity-row">
                <span className={item.status === 'accepted' ? 'text-emerald-300' : item.status === 'pending' ? 'text-amber-300' : 'text-rose-300'}>{item.status === 'accepted' ? <CheckCircle2 size={14}/> : item.status === 'pending' ? <Clock3 size={14}/> : <XCircle size={14}/>}</span>
                <span className="min-w-0 flex-1"><strong>{item.member?.name || 'Rider'}</strong><small>{item.source} · {new Date(item.createdAt).toLocaleString()}</small></span>
                <Badge tone={item.status === 'accepted' ? 'success' : item.status === 'pending' ? 'warning' : 'error'}>{item.status}</Badge>
              </div>)}
              {!selected.redemptions?.length && <p className="py-6 text-center text-xs text-slate-600">No scans yet.</p>}
            </div>
          </div>
        </>}
      </section>
    </div>
  </div>;
}
